import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { PiXBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  hasTagDelimiter,
  splitTagInput,
} from '#/modules/commonComponents/form/tagInputUtils';

/**
 * 標籤輸入（多值文字，label 固定浮在上框線；非 Floating 系列，外框對齊 shadcn Input）。
 * `value`／`onChange` 為字串陣列；Enter、逗號、blur、貼上會把草稿轉成標籤，
 * Backspace 於草稿為空時刪最後一個標籤，允許重複標籤。
 * 與後端字串的轉換由呼叫端處理（例：報修配件 `parseAccessories`／`joinAccessories`）。
 * 中文輸入法組字中不處理 Enter／分隔符，避免選字時誤建標籤。
 *
 * 灰底卡片內：容器設 `--field-bg` 即可讓 label 乾淨遮蔽上框線（與 FloatingInput 同機制）。
 */
const TagInput = ({
  id,
  value,
  onChange,
  onBlur,
  label,
  placeholder = ' ',
  errorMessage,
  disabled = false,
  containerClassName,
}) => {
  const [draft, setDraft] = useState('');
  const composingRef = useRef(false);
  const inputRef = useRef(null);
  const tags = Array.isArray(value) ? value : [];
  const hasError = Boolean(errorMessage);

  function emit(nextTags) {
    onChange(nextTags);
  }

  function commitDraft(extra = []) {
    const pending = draft.trim();
    const added = [...extra, ...(pending ? [pending] : [])];
    if (added.length > 0) emit([...tags, ...added]);
    setDraft('');
  }

  function handleChange(event) {
    const next = event.target.value;
    if (!composingRef.current && hasTagDelimiter(next)) {
      const { committed, draft: rest } = splitTagInput(next);
      if (committed.length > 0) emit([...tags, ...committed]);
      setDraft(rest);
      return;
    }
    setDraft(next);
  }

  function handleCompositionEnd(event) {
    composingRef.current = false;
    handleChange(event);
  }

  function handleKeyDown(event) {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === 'Enter') {
      event.preventDefault(); // 避免觸發整張表單 submit
      commitDraft();
      return;
    }
    if (event.key === 'Backspace' && draft === '' && tags.length > 0) {
      emit(tags.slice(0, -1));
    }
  }

  function handlePaste(event) {
    const text = event.clipboardData?.getData('text') ?? '';
    if (!hasTagDelimiter(text)) return;
    event.preventDefault();
    const { committed, draft: rest } = splitTagInput(`${draft}${text}`);
    const all = rest.trim() ? [...committed, rest.trim()] : committed;
    if (all.length > 0) emit([...tags, ...all]);
    setDraft('');
  }

  function handleBlur() {
    commitDraft();
    onBlur?.();
  }

  function removeAt(index) {
    emit(tags.filter((_, i) => i !== index));
    inputRef.current?.focus();
  }

  return (
    <div className={cn('group', containerClassName)}>
      <div className="relative">
        <div
          className={cn(
            // 外框／hover／focus 對齊 shadcn Input（rounded-sm、gray-300、primary-500 + ring-1）
            'flex min-h-12.5 w-full flex-wrap items-center gap-1.5 rounded-sm border',
            'bg-[var(--surface-field)] px-2 pb-1 pt-2 transition-colors',
            hasError
              ? cn(
                'border-red-500 ring-1 ring-red-500/35',
                'hover:border-red-500 hover:ring-red-500/35',
                'focus-within:border-red-500 focus-within:ring-red-500/35',
              )
              : cn(
                'border-gray-300',
                'hover:border-primary-500 hover:ring-1 hover:ring-primary-500/35',
                'focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500/35',
              ),
            disabled && 'opacity-60',
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {tags.map((tag, index) => (
            <span
              // 允許重複標籤，key 帶 index
              key={`${tag}-${index}`}
              className={cn(
                'inline-flex max-w-full items-center gap-1 rounded-md',
                'border border-primary-200 bg-primary-100 py-0.5 pl-2 pr-1 text-sm text-primary-900',
              )}
            >
              <span className="truncate">{tag}</span>
              {!disabled ? (
                <button
                  type="button"
                  className="rounded p-0.5 text-primary-700 hover:bg-primary-200 hover:text-primary-900"
                  aria-label={`移除 ${tag}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeAt(index);
                  }}
                >
                  <PiXBold className="size-3" aria-hidden />
                </button>
              ) : null}
            </span>
          ))}
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={draft}
            disabled={disabled}
            placeholder={placeholder}
            aria-invalid={hasError ? true : undefined}
            className="min-w-24 flex-1 bg-transparent px-1 py-1 text-sm outline-none"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleBlur}
            onCompositionStart={() => {
              composingRef.current = true;
            }}
            onCompositionEnd={handleCompositionEnd}
          />
        </div>
        {/* 對齊 Floating：label 固定浮在上框線（欄位為多標籤，無法用 placeholder-shown 切換） */}
        <label
          htmlFor={id}
          className={cn(
            'pointer-events-none absolute left-1 top-0 -translate-y-1/2 px-1 text-xs',
            // 上半＝容器底色（--field-bg）、下半＝欄位底色，遮蔽上框線
            'bg-[linear-gradient(to_bottom,var(--field-bg,var(--surface-panel))_50%,var(--surface-field)_50%)]',
            hasError
              ? 'text-red-500'
              : 'text-gray-500 group-focus-within:text-primary-600',
          )}
        >
          {label}
        </label>
      </div>
      <p className="form_error">{errorMessage || null}</p>
    </div>
  );
};

TagInput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  disabled: PropTypes.bool,
  containerClassName: PropTypes.string,
};

export default TagInput;
